import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/match-players/';
const MATCHES_API_URL = '/matches/';
const PLAYERS_API_URL = '/players/';
const TEAMS_API_URL = '/teams/';

const positionOptions = [
  { value: 'GK', label: 'Goalkeeper' },
  { value: 'DEF', label: 'Defender' },
  { value: 'MID', label: 'Midfielder' },
  { value: 'FWD', label: 'Forward' },
];

const MatchPlayerForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [matches, setMatches] = useState<{ id: number; name: string }[]>([]);
  const [players, setPlayers] = useState<{ id: number; name: string }[]>([]);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    match: '',
    player: '',
    team: '',
    position: '',
    credits: '',
    total_points: '',
  });

  useEffect(() => {
    axios.get(MATCHES_API_URL).then(res => setMatches(res.data)).catch(() => setMatches([]));
    axios.get(PLAYERS_API_URL).then(res => setPlayers(res.data)).catch(() => setPlayers([]));
    axios.get(TEAMS_API_URL).then(res => setTeams(res.data)).catch(() => setTeams([]));
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          match: res.data.match ? res.data.match.id : '',
          player: res.data.player ? res.data.player.id : '',
          team: res.data.team ? res.data.team.id : '',
          position: res.data.position || '',
          credits: res.data.credits || '',
          total_points: res.data.total_points || '',
        });
      }).catch(() => setError('Failed to load match player')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, match: form.match || null, player: form.player || null, team: form.team || null };
      if (isEdit) {
        await axios.put(API_URL + id + '/', payload);
        setSuccess('Match Player updated successfully');
      } else {
        await axios.post(API_URL, payload);
        setSuccess('Match Player created successfully');
        setForm({ match: '', player: '', team: '', position: '', credits: '', total_points: '' });
      }
      setTimeout(() => navigate('/football/match-players'), 1000);
    } catch (err: any) {
      setError('Failed to save match player');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Match Player</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField select label="Match" name="match" value={form.match} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {matches.map(match => <MenuItem key={match.id} value={match.id}>{match.name}</MenuItem>)}
          </TextField>
          <TextField select label="Player" name="player" value={form.player} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {players.map(player => <MenuItem key={player.id} value={player.id}>{player.name}</MenuItem>)}
          </TextField>
          <TextField select label="Team" name="team" value={form.team} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {teams.map(team => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}
          </TextField>
          <TextField select label="Position" name="position" value={form.position} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {positionOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField label="Credits" name="credits" value={form.credits} onChange={handleChange} type="number" fullWidth />
          <TextField label="Total Points" name="total_points" value={form.total_points} onChange={handleChange} type="number" fullWidth />
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default MatchPlayerForm; 